import { Component, Inject, ViewChild } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { EventEditConfig } from 'src/modules/event/interfaces/event-edit-config';
import { TicketDatePicker } from '../../directives/date-picker/component';
import { ITicket } from '../../interfaces/ticket';
import { IDeliveryMethod } from 'src/interfaces/delivery-method';
import { IPriceOption } from '../../interfaces/price-options';
import { IEventLocation } from 'src/modules/event/interfaces/event-location';
import { EventAddPriceOption } from '../add-price-option/component';
import { MatSelect } from '@angular/material/select';
import { TicketLocationDataSource } from '../../services/ticket-location-data-source';
import moment from 'moment';

@Component({
    selector: 'ticket-edit-dialog',
    templateUrl: 'component.html',
    styleUrls: ['component.scss'],
    standalone: false
})
export class TicketsEdit {
  @ViewChild(MatSelect) priceSelect: MatSelect;
  @ViewChild(TicketDatePicker, { static: false })
  DatePicker: TicketDatePicker;
  public objectKeys = Object.keys;
  public eventTitle: string;
  public EventLocation: IEventLocation;
  public ticketsSold: number;
  public startDate: string = null;
  public endDate: string = null;
  protected DataSource: TicketLocationDataSource;
  public priceOptions: IPriceOption[] = [
    {
      slug: 'adultPartner',
      label: 'Adult Partner',
    },
    {
      slug: 'adultSpouseDependant',
      label: 'Adult Spouse/Dependant',
    },
    {
      slug: 'partnerChildren',
      label: 'Partner Children',
    },
    {
      slug: 'adultNonPartner',
      label: 'Adult Non-Partner',
    },
    {
      slug: 'nonPartnerChildren',
      label: 'Non-Partner Children',
    },
  ];
  public deliveryMethods: IDeliveryMethod[] = [
    {
      slug: 'email',
      label: 'Email',
      features: {},
    },
    {
      slug: 'collection',
      label: 'Collection',
      features: {
        product: true,
      },
    },
    {
      slug: 'post',
      label: 'Post',
      features: {
        product: true,
        post: true,
      },
    },
  ];
  private productId: string;
  public Ticket: ITicket;
  public form: FormGroup;

  public get prices(): FormArray {
    return this.form.get('prices') as FormArray;
  }

  protected resetEvent() {
    this.Ticket.anarchy = !!this.Ticket['features'].anarchy;
    this.Ticket.calendar = !!this.Ticket['features'].calendar;
    Object.keys(this.Ticket).forEach((name) => {
      const control = this.form.get(name);

      if (!!control && 'prices' !== name) {
        control.patchValue(this.Ticket[name]);
      }
    });
  }

  constructor(
    protected dialog: MatDialog,
    private dataSource: TicketLocationDataSource,
    @Inject(MAT_DIALOG_DATA) protected data: EventEditConfig,
    protected DialogRef: MatDialogRef<EventAddPriceOption>
  ) {
    this.DataSource = dataSource;
    const {
      EventTitle: eventTitle,
      EventLocation: eventLocation,
      Ticket,
      ProductId,
    } = this.data;
    const { ticketsSold = 0 } = Ticket || {};

    this.productId = ProductId;
    this.eventTitle = eventTitle;
    this.EventLocation = eventLocation;
    this.Ticket = Ticket;
    this.startDate = moment(Ticket.startDate, 'DD/MM/YYYY').toISOString();
    this.ticketsSold = ticketsSold;
  }

  ngOnInit() {
    this.buildForm();
  }

  buildForm() {
    const builder = new FormBuilder();
    this.form = builder.group({
      locationId: [this.EventLocation.companyLocationId],
      totalTickets: [
        this.Ticket?.totalTickets && this.Ticket.totalTickets,
        [Validators.required, Validators.min(this.ticketsSold)],
      ],
      ticketsPerPartner: [
        this.Ticket?.ticketsPerPartner && this.Ticket.ticketsPerPartner,
        [Validators.required, Validators.min(0)],
      ],
      selectPriceOption: [null],
      prices: new FormControl(
        Object.values(JSON.parse(JSON.stringify(this.Ticket.prices))),
        [Validators.required],
        this.validatePrices.bind(this)
      ),
      deliveryMethod: [this.Ticket?.deliveryMethod, [Validators.required]],
      startDate: [
        this.Ticket?.startDate && this.Ticket.startDate,
        [Validators.required],
      ],
      startTime: [this.Ticket?.startTime && this.Ticket.startTime],
      endDate: [this.Ticket?.endDate],
      anarchy: [this.Ticket?.anarchy],
      calendar: [this.Ticket?.calendar],
      stagedRelease: [this.Ticket?.stagedRelease],
      stagedReleaseDate: [this.Ticket?.releaseDate],
    });
  }

  protected validatePrices(Prices: FormArray) {
    const errors: ValidationErrors = {};
    !Prices.value.length &&
      (errors['noPrices'] =
        'Please add at least one price option for this event.');

    return Promise.resolve(!Object.keys(errors).length ? null : errors);
  }

  public onSubmit() {
    this.form.disable();
    const { id: ticketId = null } = this.Ticket || {},
      EventData = this.form.getRawValue();
    this.DataSource.save(this.productId, ticketId, EventData)
      .then(() => this.DialogRef.close(true))
      .catch(() => this.form.enable());
  }

  public resetForm() {
    this.buildForm();

    this.DatePicker.ParentFormGroup = this.form;
    this.DatePicker.startDate = this.Ticket?.startDate;
    this.DatePicker.endDate = this.Ticket?.endDate;
    this.DatePicker.ngOnInit();
  }

  public addOption() {
    const config = new MatDialogConfig();
    config.disableClose = true;

    config.data = {};

    this.dialog
      .open(EventAddPriceOption, config)
      .afterClosed()
      .subscribe((priceOption?: IPriceOption) => {
        this.priceOptions.push(priceOption);
        this.addPriceOption(priceOption);
      });
  }

  public addPriceOption(priceOption?: IPriceOption) {
    let options = this.form.controls['prices'].value;
    if (!options[0]) {
      options = [];
    }
    if (priceOption) {
      options.push({ ...priceOption, price: 0 });
    }

    this.form.controls['prices'].patchValue(options);
    this.priceSelect.close();
  }

  public removePriceOption(index: number) {
    let options = this.form.controls['prices'].value;
    options.splice(index, 1);
    this.form.controls['prices'].patchValue(options);
  }

  public filterPriceOptions() {
    let usedPrices = this.form.controls['prices'].value;

    return this.priceOptions.filter((option) => {
      return !usedPrices.filter(
        (usedPrice) => option.slug === usedPrice.slug
      )[0];
    });
  }

  public updatePrice(price) {
    let options = this.form.get('prices').value;
    options.map((item) => {
      if (item.label === price.id) {
        item.price = price.value;
      }
      return item;
    });
    this.form.get('prices').patchValue(options);
  }
}
